// ============================================================================
// COMPONENTE: PortalNPS.jsx — C2
// Mini-survey de satisfação disparado ao cliente quando a etapa do caso muda.
// Coleção respostas: artifacts/{appId}/public/data/portal_nps
// Após envio: workspace recebe npsCompletedForStage = npsForStage
// ============================================================================

import React, { useState } from 'react';
import { Star, X } from 'lucide-react';
import { submitNps, markWorkspaceNpsDone } from '../../services/PortalNPSRepository';

const STAGE_LABEL = {
  pre_processual:  'Fase Pré-Processual',
  peticao_inicial: 'Petição Inicial',
  em_andamento:    'Fase de Instrução',
  recurso:         'Recurso',
  encerrado:       'Encerramento do Caso',
};

const SCORE_LABELS = ['', 'Muito ruim', 'Ruim', 'Regular', 'Bom', 'Excelente'];

export default function PortalNPS({ workspaceId, clientId, clientName, stage, onDone }) {
  const [score, setScore]     = useState(0);
  const [hover, setHover]     = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving]   = useState(false);
  const [sent, setSent]       = useState(false);

  const stageLabel = STAGE_LABEL[stage] || stage || 'Etapa atual';

  const handleSubmit = async () => {
    if (!score || saving) return;
    setSaving(true);
    try {
      await submitNps({ clientId, clientName, workspaceId, stage, score, comment: comment.trim() || null });
      if (workspaceId) {
        await markWorkspaceNpsDone(workspaceId, stage);
      }
      setSent(true);
      setTimeout(() => onDone(), 2200);
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-[#20243B] px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-[#C5A059] mb-0.5">
              Seu escritório pergunta
            </div>
            <div className="text-sm font-bold text-white leading-snug">
              Como avalia o atendimento na<br />
              <span className="text-[#C5A059]">{stageLabel}</span>?
            </div>
          </div>
          <button
            type="button"
            onClick={onDone}
            className="mt-0.5 h-6 w-6 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {sent ? (
          <div className="px-5 py-8 text-center">
            <div className="text-3xl mb-3">🙏</div>
            <div className="text-sm font-bold text-[#20243B]">Obrigado pela avaliação!</div>
            <div className="text-xs text-gray-500 mt-1">Sua opinião ajuda a melhorar o atendimento.</div>
          </div>
        ) : (
          <div className="px-5 py-5 space-y-4">
            {/* Estrelas */}
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setScore(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="transition-transform motion-safe:hover:scale-110"
                >
                  <Star
                    className={`w-9 h-9 transition-colors ${
                      n <= (hover || score)
                        ? 'text-[#C5A059] fill-[#C5A059]'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Label da nota */}
            <div className="text-center text-xs font-semibold text-gray-500 h-4">
              {SCORE_LABELS[hover || score] || ''}
            </div>

            {/* Comentário opcional */}
            {score > 0 && (
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Algum comentário? (opcional)"
                rows={2}
                className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 outline-none focus:border-[#20243B] resize-none"
              />
            )}

            {/* Ações */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!score || saving}
                className="flex-1 bg-[#20243B] text-white text-xs font-bold rounded-lg py-2.5 hover:bg-[#C5A059] hover:text-[#20243B] transition-colors disabled:opacity-40"
              >
                {saving ? 'Enviando...' : 'Enviar avaliação'}
              </button>
              <button
                type="button"
                onClick={onDone}
                className="px-3 border border-gray-300 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition-colors"
              >
                Pular
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
